import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─── Quick Access Links ─────────────────────────────────────────
   Each entry needs:
     label  → display text
     to     → route path
     hash   → (optional) id of element to scroll to on that page
   ---------------------------------------------------------------- */
const QUICK_ACCESS_LINKS = [
  { label: "Hekaos Articles",        to: "/resources/articles"      },
  { label: "Heka Atlas",             to: "/products/HekaAtlas"      },    // Ats 
  { label: "Heka Codex",             to: "/products/HekaCodex"      },    // Client
  { label: "Heka Odyssey",           to: "/products/HekaOdyssey"    },    // Candidate
  { label: "Hekaos Philosophy",      to: "/vision", hash: "jump-to-philosophy" },
  { label: "Hekaos Pillars",         to: "/vision", hash: "jump-to-pillars" },
  { label: "Hekaos Manifesto",       to: "/vision", hash: "jump-to-manifesto" },
];

/* ─── Component ────────────────────────────────────────────────── */
const RightSidebar = ({ isOpen, toggle, setCurrentPage }) => {
  const navigate = useNavigate();

  const handleLinkClick = (to, hash) => (e) => {
    e.preventDefault();

    if (hash) {
      // Navigate to the route first, then scroll after React renders the page
      navigate(to);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      navigate(to);
    }

    if (window.innerWidth < 1024) toggle();
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggle}
      />

      <aside
        className={`fixed lg:sticky top-0 right-0 h-screen bg-surface backdrop-blur-sm border-l border-subtle z-50 transition-all duration-300 ${
          isOpen ? 'translate-x-0 lg:w-64' : 'translate-x-full lg:translate-x-0 lg:w-0'
        }`}
      >
        <div className={`${isOpen ? 'block' : 'hidden lg:hidden'} w-64`}>

          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-subtle lg:hidden">
            <span className="accent-amber font-bold">Info</span>
            <button onClick={toggle} className="accent-amber hover:text-primary transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">

            {/* System Status */}
            <div className="bg-overlay p-3 rounded border border-subtle">
              <h3 className="accent-amber text-sm font-bold mb-2">System Status</h3>
              <div className="space-y-1 text-xs text-secondary">
                <div className="flex justify-between">
                  <span>Uptime</span>
                  <span className="text-green-400">99.9%</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Nodes</span>
                  <span className="text-amber-400">47</span>
                </div>
                <div className="flex justify-between">
                  <span>Intelligence</span>
                  <span className="text-violet-400">Optimal</span>
                </div>
              </div>
            </div>

            {/* Quick Access */}
            <div className="bg-overlay p-3 rounded border border-subtle">
              <h3 className="accent-amber text-sm font-bold mb-2">Quick Access</h3>
              <ul className="space-y-1 text-xs text-secondary">
                {QUICK_ACCESS_LINKS.map(({ label, to, hash }) => (
                  <li key={`${to}${hash || ''}`}>
                    <a
                      href={hash ? `${to}#${hash}` : to}
                      onClick={handleLinkClick(to, hash)}
                      className="accent-amber hover:text-primary transition-colors cursor-pointer"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Desktop collapse toggle */}
        <button
          onClick={toggle}
          className="hidden lg:flex absolute -left-6 top-20 bg-surface border border-subtle p-1 rounded-l accent-amber hover:bg-surface-elevated transition-colors"
          title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>
    </>
  );
};

export default RightSidebar;